/**
 * Centralized data-testid identifiers for proposal builder components.
 * Used by browser-based QA automation to locate UI elements reliably.
 *
 * Convention: kebab-case, {descriptive-name}-{role}
 * Roles: -button, -input, -dropdown, -modal, -section, -row, -table
 *
 * Per-row / per-option testids: callers append a stable id at render time
 * (e.g. `${COMPONENT_IDS.actions.lockButton}-${allocationId}`), or use the
 * `statisticsValue` helper below.
 */
export const COMPONENT_IDS = {
  // Proposals list page
  proposals: {
    createProposalButton: 'create-proposal-button',
    uploadHoldingsButton: 'upload-holdings-button',
    proposalRow: 'proposal-row', // append `-${id}` at render time
  },

  // Template modal
  templateModal: {
    modal: 'template-modal',
    templateOption: 'template-option', // append `-${slugified name}` at render time
    templateOptionName: 'template-option-name', // append `-${slugified name}` at render time
    useTemplateButton: 'template-modal-use-template-button',
  },

  // Proposal builder sections
  sections: {
    navbar: 'navbar-section',
    header: 'header-section',
    toolbar: 'toolbar-section',
    investorInfo: 'investor-info-section',
    modelInformation: 'model-information-section',
    sleeves: 'sleeves-section',
    diversificationParams: 'diversification-params-section',
    rulesList: 'rules-list-section',
    statistics: 'statistics-section',
  },

  // Investor info
  investorInfo: {
    investorNameInput: 'investor-name-input',
    taxProfileDropdown: 'tax-profile-dropdown',
    totalInvestmentInput: 'total-investment-input',
    tradingRiskGroupDropdown: 'trading-risk-group-dropdown',
  },

  // Model information (model builder)
  modelInformation: {
    currencyDropdown: 'model-currency-dropdown',
    sampleCashInput: 'model-sample-cash-input',
    cashBufferInput: 'model-cash-buffer-input',
    descriptionInput: 'model-description-input',
  },

  // Diversification params
  diversification: {
    weightPerBondMinInput: 'weight-per-bond-min-input',
    weightPerBondMaxInput: 'weight-per-bond-max-input',
    maxBondsPerIssuerInput: 'max-bonds-per-issuer-input',
    maxBondsPerUltimateParentInput: 'max-bonds-per-ultimate-parent-input',
    maxBondsPerUltimateBorrowerInput: 'max-bonds-per-ultimate-borrower-input',
    uniqueBondsMinInput: 'unique-bonds-min-input',
    uniqueBondsMaxInput: 'unique-bonds-max-input',
    uniqueIssuersMinInput: 'unique-issuers-min-input',
    uniqueIssuersMaxInput: 'unique-issuers-max-input',
    uniqueUltimateParentsMinInput: 'unique-ultimate-parents-min-input',
    uniqueUltimateParentsMaxInput: 'unique-ultimate-parents-max-input',
    uniqueUltimateBorrowersMinInput: 'unique-ultimate-borrowers-min-input',
    uniqueUltimateBorrowersMaxInput: 'unique-ultimate-borrowers-max-input',
  },

  // Floating toolbar
  toolbar: {
    objectiveFunctionButton: 'objective-function-button',
    layoutControlDropdown: 'layout-control-dropdown',
  },

  // Per-constraint-row UI in the risk-factor / rules list
  constraints: {
    complianceIcon: 'compliance-icon', // append `-${complianceIconTestidSuffix(state)}` at render time
    deleteButton: 'constraint-delete-button', // append `-${constraintId}` at render time
    restoreButton: 'constraint-restore-button', // append `-${constraintId}` at render time
    buyOnlyBadge: 'constraint-buy-only-badge', // append `-${constraintId}` at render time
  },

  // Top bar (nav actions)
  navbar: {
    useTemplateButton: 'use-template-button',
    optimizeButton: 'optimize-button',
    optimizeMinimizeButton: 'optimize-minimize-button',
    optimizeMaximizeButton: 'optimize-maximize-button',
    submitTradesButton: 'submit-trades-button',
    exportButton: 'export-button',
    exportCsvButton: 'export-csv-button',
    csvColumnsButton: 'csv-columns-button',
    exportPdfButton: 'export-pdf-button',
    exportReportButton: 'export-report-button',
    shareProposalButton: 'share-proposal-button',
    publishModelButton: 'publish-model-button',
    deleteModelButton: 'delete-model-button',
    overflowMenu: 'navbar-overflow-menu',
    proposalSetupToggle: 'proposal-setup-toggle',
    versionHistoryToggle: 'version-history-toggle',
  },

  // Per-stat banner values (Holdings + Universe). Use `statisticsValue(key)`.
  statistics: {
    value: 'statistics-value', // append `-${key}` at render time
  },

  // Per-row action icons (universe / holdings / actions tables).
  // Suffix with stable row id at render time: ISIN or proposal_allocation_id.
  actions: {
    lockButton: 'action-lock',
    replaceButton: 'action-replace',
    deleteButton: 'action-delete',
    addToFilterListButton: 'action-add-to-filter-list',
  },

  // Inline cell inputs
  inputs: {
    quantityInput: 'quantity-input', // append `-${allocationId}` at render time
  },

  tables: {
    universe: 'universe-table',
    holdings: 'holdings-table',
    actions: 'actions-table',
    replacementBonds: 'replacement-bonds-table',
    searchableReplacementBonds: 'searchable-replacement-bonds-table',
  },

  // Optimizer loading overlay
  loading: {
    optimizerScreen: 'optimizer-loading-screen',
  },

  // Dialogs
  dialogs: {
    deleteConfirmation: {
      root: 'delete-confirmation-dialog',
      confirmButton: 'delete-confirmation-dialog-confirm',
      cancelButton: 'delete-confirmation-dialog-cancel',
    },
    confirmProposalOrderRemove: {
      root: 'confirm-proposal-order-remove-dialog',
      confirmButton: 'confirm-proposal-order-remove-dialog-confirm',
      cancelButton: 'confirm-proposal-order-remove-dialog-cancel',
    },
    generatePdf: {
      root: 'generate-pdf-dialog',
      confirmButton: 'generate-pdf-dialog-confirm',
      cancelButton: 'generate-pdf-dialog-cancel',
    },
    shareProposal: {
      root: 'share-proposal-dialog',
      confirmButton: 'share-proposal-dialog-confirm',
      cancelButton: 'share-proposal-dialog-cancel',
    },
    confirmSubmitProposal: {
      root: 'confirm-submit-proposal-dialog',
      confirmButton: 'confirm-submit-proposal-dialog-confirm',
      cancelButton: 'confirm-submit-proposal-dialog-cancel',
    },
    addToFilterList: {
      root: 'add-to-filter-list-dialog',
      confirmButton: 'add-to-filter-list-dialog-confirm',
      cancelButton: 'add-to-filter-list-dialog-cancel',
    },
  },

  // Version history panel
  versionHistory: {
    versionADropdown: 'version-a-dropdown',
    versionBDropdown: 'version-b-dropdown',
  },

  // Proposal editor (model + state of residence pickers)
  editor: {
    modelDropdown: 'editor-model-dropdown',
    stateOfResidenceDropdown: 'editor-state-of-residence-dropdown',
  },

  // Risk factor autocomplete
  search: {
    riskFactorDropdown: 'risk-factor-search-dropdown',
  },
} as const

/**
 * Build a stable testid for a single stat value in the statistics banners.
 * Underscores in the key (e.g. StatId.OrderMV = 'order_mv') are normalized
 * to dashes so the rendered attribute matches the rest of the registry.
 *
 *   statisticsValue('order_mv')      => 'statistics-value-order-mv'
 *   statisticsValue('eligible-bonds') => 'statistics-value-eligible-bonds'
 */
export function statisticsValue(key: string): string {
  return `${COMPONENT_IDS.statistics.value}-${key.replace(/_/g, '-')}`
}

/**
 * Build a stable testid for a per-row interactive element (action button,
 * inline input, etc). The shape is `<action>-<rowId>`, where `rowId` is the
 * bond's ISIN — chosen for human-readability in tests.
 *
 *   generateCellId(COMPONENT_IDS.actions.lockButton, 'US912828YY09')
 *   => 'action-lock-US912828YY09'
 */
export function generateCellId(actionTestId: string, rowId: string): string {
  return `${actionTestId}-${rowId}`
}

/**
 * Map a RuleVerdictState to its testid suffix. The `tbd` state is
 * rendered as `neutral` so QA selectors read naturally
 * (`compliance-icon-neutral` vs `compliance-icon-tbd`).
 */
export function complianceIconTestidSuffix(
  state: 'compliant' | 'non_compliant' | 'relaxed' | 'tbd' | 'loading',
): 'compliant' | 'non-compliant' | 'relaxed' | 'neutral' | 'loading' {
  switch (state) {
    case 'compliant':
      return 'compliant'
    case 'relaxed':
      return 'relaxed'
    case 'non_compliant':
      return 'non-compliant'
    case 'tbd':
      return 'neutral'
    case 'loading':
      return 'loading'
  }
}

/**
 * Build a stable testid for an option inside a dropdown / select / menu.
 * Required because Radix portals options to `document.body`, outside the
 * trigger's DOM subtree — options must be globally addressable.
 *
 *   dropdownOption('tax-profile', 'us_taxable')
 *   => 'tax-profile-option-us_taxable'
 */
export function dropdownOption(name: string, value: string): string {
  return `${name}-option-${value}`
}
